import type { UseFormReturnType } from "@mantine/form";
import type { _TransformValues } from "octalog-base";
import { useCallback } from "react";
import type { RefObject } from "react";

import { LocalTimeInternal } from "../../../../../common/calendar/LocalTimeInternal";
import type { OrderFormData } from "../contexts/FormContexts";

// Constants
const DEFAULT_TIME_COMPONENT = "00";
const HOURS_MAX = 23;
const MINUTES_MAX = 59;

// Types
interface TimePickerConfig {
  fieldName: keyof OrderFormData;
  hoursRef: RefObject<HTMLInputElement | null>;
  minutesRef: RefObject<HTMLInputElement | null>;
  form: UseFormReturnType<OrderFormData, _TransformValues<OrderFormData>>;
}

// Pure validation functions
function validateTimeComponent(value: string, max: number): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const num = Number.parseInt(trimmed, 10);
  if (Number.isNaN(num) || num < 0 || num > max) {
    return null;
  }
  return num;
}

function formatTimeComponent(value: number): string {
  return value.toString().padStart(2, "0");
}

function createTimeString(hours?: string, minutes?: string): string | undefined {
  const h = (hours ?? "").trim();
  const m = (minutes ?? "").trim();

  if (!h && !m) return undefined;

  // Validate and format hours
  const hourNum = h ? validateTimeComponent(h, HOURS_MAX) : 0;
  if (h && hourNum === null) return undefined; // Invalid hours

  // Validate and format minutes
  const minuteNum = m ? validateTimeComponent(m, MINUTES_MAX) : 0;
  if (m && minuteNum === null) return undefined; // Invalid minutes

  const normalizedHours = formatTimeComponent(hourNum ?? 0);
  const normalizedMinutes = formatTimeComponent(minuteNum ?? 0);

  return `${normalizedHours}:${normalizedMinutes}`;
}

function updateInputRefs(
  hoursRef: RefObject<HTMLInputElement | null>,
  minutesRef: RefObject<HTMLInputElement | null>,
  time: string,
): void {
  const [hh = DEFAULT_TIME_COMPONENT, mm = DEFAULT_TIME_COMPONENT] = time.split(":");

  if (hoursRef.current) hoursRef.current.value = hh;
  if (minutesRef.current) minutesRef.current.value = mm;
}

function safeCreateLocalTime(timeString: string): LocalTimeInternal | null {
  try {
    return LocalTimeInternal.fromString(timeString);
  } catch {
    return null;
  }
}

/**
 * Custom hook for TimePicker functionality with input validation and normalization
 */
export function useTimePicker({ fieldName, hoursRef, minutesRef, form }: TimePickerConfig) {
  const handleChange = useCallback(
    (value: string) => {
      if (value === "") {
        // Handle empty value by normalizing ref inputs
        const normalizedTime = createTimeString(hoursRef.current?.value, minutesRef.current?.value);

        if (normalizedTime) {
          const localTime = safeCreateLocalTime(normalizedTime);
          if (localTime) {
            updateInputRefs(hoursRef, minutesRef, normalizedTime);
            form.setFieldValue(fieldName, localTime);
            return;
          }
        }

        // Clear field if normalization failed or inputs are empty
        form.setFieldValue(fieldName, undefined);
      } else {
        // Handle direct value input
        const localTime = safeCreateLocalTime(value);
        if (localTime) {
          form.setFieldValue(fieldName, localTime);
        } else {
          form.setFieldValue(fieldName, undefined);
        }
      }
    },
    [fieldName, hoursRef, minutesRef, form],
  );

  const handleBlur = useCallback(() => {
    const h = hoursRef.current?.value;
    const m = minutesRef.current?.value;
    const normalizedTime = createTimeString(h, m);

    if (normalizedTime) {
      const localTime = safeCreateLocalTime(normalizedTime);
      if (localTime) {
        updateInputRefs(hoursRef, minutesRef, normalizedTime);
        form.setFieldValue(fieldName, localTime);
        return;
      }
    }

    // Clear field if both inputs are empty
    if (!h && !m) {
      form.setFieldValue(fieldName, undefined);
    }
  }, [fieldName, hoursRef, minutesRef, form]);

  return {
    handleChange,
    handleBlur,
  };
}
